import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateInput from "@/components/common/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const TEXT_FIELDS = [
  ["org_name", "Organization *"], ["contact_person", "Contact person"], ["mobile", "Mobile"], ["whatsapp", "WhatsApp"],
  ["email", "Email"], ["city", "City"], ["address", "Address"], ["website", "Website"], ["next_action", "Next action"],
];
const KEYS = [...TEXT_FIELDS.map((f) => f[0]), "lead_type", "source", "status", "estimated_value", "requirement", "notes", "next_action_date"];

function Pick({ label, value, options, onChange, testid }) {
  return (
    <div><Label>{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

/** Super Admin only — the backend rejects edits from every other role. */
export default function LeadEditDialog({ open, onOpenChange, lead, onSaved }) {
  const meta = useMeta();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && lead) setForm(Object.fromEntries(KEYS.map((k) => [k, lead[k] ?? ""])));
  }, [open, lead]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!String(form.org_name || "").trim()) return toast.error("Organization name is required");
    setBusy(true);
    try {
      const payload = { ...form, estimated_value: form.estimated_value === "" ? null : Number(form.estimated_value) };
      const { data } = await api.patch(`/leads/${lead.id}`, payload);
      toast.success("Lead updated");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit lead</DialogTitle>
          <DialogDescription>Super Admin edit. Changes are recorded in the audit log.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {TEXT_FIELDS.map(([k, label]) => (
            <div key={k}><Label>{label}</Label>
              <Input className="mt-1.5" value={form[k] || ""} onChange={(e) => set(k)(e.target.value)} data-testid={`lead-edit-${k}`} />
            </div>
          ))}
          <div><Label>Next action date</Label><DateInput className="mt-1.5 h-10" value={(form.next_action_date || "").slice(0, 10)} onChange={set("next_action_date")} testid="lead-edit-next_action_date" /></div>
          <Pick label="Status" value={form.status} options={meta.lead_statuses || []} onChange={set("status")} testid="lead-edit-status" />
          <Pick label="Lead type" value={form.lead_type} options={meta.lead_types || []} onChange={set("lead_type")} testid="lead-edit-lead_type" />
          <Pick label="Source" value={form.source} options={meta.lead_sources || []} onChange={set("source")} testid="lead-edit-source" />
          <div><Label>Estimated value (₹)</Label><Input type="number" min="0" className="mt-1.5" value={form.estimated_value ?? ""} onChange={(e) => set("estimated_value")(e.target.value)} data-testid="lead-edit-estimated_value" /></div>
          <div className="sm:col-span-2"><Label>Requirement</Label><Textarea rows={2} className="mt-1.5" value={form.requirement || ""} onChange={(e) => set("requirement")(e.target.value)} data-testid="lead-edit-requirement" /></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} className="mt-1.5" value={form.notes || ""} onChange={(e) => set("notes")(e.target.value)} data-testid="lead-edit-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="lead-edit-save">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
